import { routeStore } from '../state/routeStore';
import { t } from '../i18n';
import { displayDurationSeconds } from '../lib/activeWaypoint';
import { Avatar } from './Avatar';

/**
 * The arrival-state panel takes over the status hero once `route.finished`.
 * Calmer than the live state — green check, summary numbers. The
 * replay-the-journey toggle lives in the drawer (see ReplayButton) so the
 * arrival hero stays focused on the "they made it" message.
 */
export function ArrivalState() {
  const route = routeStore.value;
  if (!route?.finished) return null;
  const name = route.creator?.name?.trim() || route.creator?.username || '—';
  const time = formatFinishTime(route.finished_at);
  const headline = time
    ? t('status.arrived', { name, time })
    : t('status.arrivedNoTime', { name });
  const summary = formatSummary(route);

  return (
    <div class="pointer-events-auto inline-flex w-full max-w-xl items-start gap-3 rounded-2xl bg-surface px-4 py-3 shadow-md border border-safety/30">
      <Avatar
        photoUrl={route.creator?.photo?.medium}
        name={route.creator?.name ?? route.creator?.username}
        size={44}
        status="arrived"
      />
      <div class="min-w-0">
        <h1 class="text-base font-semibold text-primary">{headline}</h1>
        {summary && <p class="text-sm text-text-secondary mt-0.5">{summary}</p>}
      </div>
    </div>
  );
}

function formatFinishTime(finishedAt: string | null | undefined): string | null {
  if (!finishedAt) return null;
  const date = new Date(finishedAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatSummary(route: NonNullable<typeof routeStore.value>): string {
  const parts: string[] = [];
  if (route.distance && route.distance > 0) {
    parts.push(route.distance < 1_000 ? `${Math.round(route.distance)} m` : `${(route.distance / 1_000).toFixed(1)} km`);
  }
  // Prefers the server-computed duration (api #180); falls back to the
  // created_at→finished_at spread for routes from before it shipped.
  const durationSeconds = displayDurationSeconds(route);
  if (durationSeconds !== null) {
    const minutes = Math.round(durationSeconds / 60);
    if (minutes > 0) parts.push(`${minutes} min`);
  }
  return parts.join(' · ');
}
